// Helper function for Server-Sent Events (SSE) with authentication
export interface SSEOptions {
    url: string;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    onComplete?: () => void;
    signal?: AbortSignal;
}

const checkTokenExpiry = (token: any, bufferSeconds = 0): boolean => {
    try {
        if (!token || typeof token !== 'string') return true;
        const parts = token.split('.');
        if (parts.length !== 3) return true;
        const tokenData = JSON.parse(atob(parts[1]));
        return Date.now() >= (tokenData.exp * 1000 - bufferSeconds * 1000);
    } catch {
        return true;
    }
};

const buildApiUrl = (path: string): string => {
    const host = process.env.REACT_APP_API_HOST_DEV;
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    return window.location.host === host && baseURL ? baseURL + path : path;
};

const refreshAccessToken = async (): Promise<string> => {
    const raw = localStorage.getItem('user');
    if (!raw) throw new Error('No user data');
    const userData = JSON.parse(raw);
    const { access, refresh } = userData?.tokens || {};

    if (!refresh || checkTokenExpiry(refresh)) {
        localStorage.clear();
        window.location.href = '/login';
        throw new Error('Refresh token expired');
    }

    const response = await fetch(buildApiUrl('/users/token-refresh'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${access}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
    });

    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            localStorage.clear();
            window.location.href = '/login';
        }
        throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    const updatedUser = { ...userData, tokens: { ...userData.tokens, access: data.access } };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    return data.access;
};

const getValidToken = async (): Promise<string> => {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    try {
        const userData = JSON.parse(raw);
        const token = userData?.tokens?.access || '';
        if (checkTokenExpiry(token, 30)) {
            return await refreshAccessToken();
        }
        return token;
    } catch (error) {
        console.error('Error getting valid token:', error);
        return '';
    }
};

export const createSSEConnection = ({ url, onMessage, onError, onComplete, signal }: SSEOptions) => {
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let isCompleted = false;
    let aborted = false;

    const connect = async (retrying = false): Promise<void> => {
        let token = '';
        try {
            token = await getValidToken();
        } catch (error) {
            if (onError) onError(error);
            return;
        }

        const fullUrl = buildApiUrl(url);

        try {
            // Use fetch with streaming instead of EventSource to support custom headers
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                signal,
            });

            if (!response.ok) {
                // Retry once with a fresh token on 401
                if (response.status === 401 && !retrying) {
                    try {
                        await refreshAccessToken();
                    } catch (refreshError) {
                        if (onError) onError(refreshError);
                        return;
                    }
                    return connect(true);
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (!aborted && !isCompleted) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // Decode the chunk and add to buffer
                buffer += decoder.decode(value, { stream: true });

                // Process complete SSE messages
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6); // Remove 'data: ' prefix

                        try {
                            const data = JSON.parse(dataStr);

                            // Check if this is the complete event
                            if (data.status === 'complete') {
                                isCompleted = true;
                                if (onComplete) {
                                    onComplete();
                                }
                                cleanup();
                                return;
                            }

                            // Pass data to the message handler
                            onMessage(data);
                        } catch (parseError) {
                            console.error('Error parsing SSE data:', parseError, dataStr);
                            if (onError) {
                                onError(parseError);
                            }
                        }
                    }
                }
            }

            cleanup();
        } catch (error: any) {
            if (error.name !== 'AbortError' && !aborted) {
                console.error('SSE connection error:', error);
                if (onError && !isCompleted) {
                    onError(error);
                }
            }
            cleanup();
        }
    };

    const cleanup = () => {
        aborted = true;
        if (reader) {
            reader.cancel().catch(() => {});
            reader = null;
        }
    };

    // Handle abort signal
    if (signal) {
        signal.addEventListener('abort', () => {
            cleanup();
        });
    }

    // Start the connection
    connect();

    // Return cleanup function
    return cleanup;
};
